import { useMutation } from '@apollo/client/react';
import { useCallback, useMemo, useState } from 'react';

import { CREATE_PERSON, errorCode, ME } from '@/account/arcade-api';
import { useAuth } from '@/auth/useAuth';
import { AuthScreen } from '@/components/AuthScreen/AuthScreen';
import {
  CreateAccountForm,
  type AccountDetails,
} from '@/components/CreateAccountForm/CreateAccountForm';
import { environmentName } from '@/config/runtime-config';

/**
 * For a signed-in identity with no Person yet — a sign-up whose account step
 * failed, or someone who verified a pin without going through sign-up.
 */
export function CompleteAccountContainer() {
  const { identity, signOut } = useAuth();
  const environment = useMemo(() => environmentName(), []);
  const [error, setError] = useState<string | null>(null);
  const [createPerson, { loading }] = useMutation(CREATE_PERSON, {
    refetchQueries: [{ query: ME }],
    awaitRefetchQueries: true,
  });

  const onSubmit = useCallback(
    async (details: AccountDetails) => {
      setError(null);
      try {
        await createPerson({ variables: details });
      } catch (failure) {
        if (errorCode(failure) === 'EMAIL_MISMATCH') {
          setError('That email does not match the one you signed in with.');
        } else if (errorCode(failure) !== 'PERSON_EXISTS') {
          setError('Could not create your account. Try again.');
        }
      }
    },
    [createPerson],
  );

  return (
    <AuthScreen environment={environment}>
      <CreateAccountForm
        initial={{ email: identity?.email ?? '' }}
        emailLocked={Boolean(identity?.email)}
        pending={loading}
        error={error}
        submitLabel="Create account"
        onSubmit={onSubmit}
        secondaryLabel="Sign out"
        onSecondary={signOut}
      />
    </AuthScreen>
  );
}
